import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import DashboardHeader from '../../components/common/DashboardHeader';
import {
  UsersIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  KeyIcon,
  ArrowPathIcon,
  EyeIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface User {
  id: number;
  name: string;
  email: string;
  phone_number: string;
  role: string;
  garage_id: number | null;
  garage?: { name: string; id: number };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface UserStats {
  by_role: Record<string, number>;
  active_users: number;
  inactive_users: number;
  recent_registrations: number;
  by_garage: Array<{
    garage_id: number;
    garage_name: string;
    user_count: number;
  }>;
}

interface UserDetail {
  user: User;
  activity_count: number;
  recent_activities: any[];
  assigned_jobs?: number;
  completed_jobs?: number;
  garage_stats?: any;
  vehicle_count?: number;
  service_count?: number;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [garageFilter, setGarageFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchUserStats();
  }, [page, roleFilter, statusFilter, garageFilter, search]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users', {
        params: {
          page,
          limit: 20,
          role: roleFilter,
          status: statusFilter === 'active' ? 'active' : statusFilter === 'inactive' ? 'inactive' : '',
          garage_id: garageFilter,
          search,
        },
      });
      setUsers(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await api.get('/admin/users/stats');
      setUserStats(response.data.data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const fetchUserDetails = async (userId: number) => {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      setUserDetail(response.data.data);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Failed to load user details');
    }
  };

  const updateUserStatus = async (userId: number, isActive: boolean, reason: string = '') => {
    try {
      await api.put(`/admin/users/${userId}/status`, { 
        is_active: isActive, 
        reason: reason || (isActive ? 'Activated by admin' : 'Deactivated by admin')
      });
      toast.success(`User ${isActive ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
      fetchUserStats();
    } catch (error: any) {
      console.error('Error updating user status:', error);
      const errorMessage = error.response?.data?.message || (error as any)?.message || 'Failed to update user status';
      toast.error(errorMessage);
    }
  };

  const updateUserRole = async (userId: number, role: string, reason: string) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role, reason });
      toast.success('User role updated successfully');
      fetchUsers();
      fetchUserStats();
      setShowEditModal(false);
    } catch (error: any) {
      console.error('Error updating user role:', error);
      const errorMessage = error.response?.data?.message || (error as any)?.message || 'Failed to update user role';
      toast.error(errorMessage);
    }
  };

  const resetUserPassword = async (userId: number) => {
    try {
      const response = await api.post(`/admin/users/${userId}/reset-password`, {
        send_notification: true
      });
      setTempPassword(response.data.temp_password);
      setShowPasswordModal(true);
      toast.success('Password reset successfully');
    } catch (error: any) {
      console.error('Error resetting password:', error);
      const errorMessage = error.response?.data?.message || (error as any)?.message || 'Failed to reset password';
      toast.error(errorMessage);
    }
  };

  const transferUser = async (userId: number, newGarageId: number, reason: string) => {
    try {
      await api.post(`/admin/users/${userId}/transfer`, {
        new_garage_id: newGarageId,
        reason
      });
      toast.success('User transferred successfully');
      fetchUsers();
      setShowTransferModal(false);
    } catch (error: any) {
      console.error('Error transferring user:', error);
      const errorMessage = error.response?.data?.message || (error as any)?.message || 'Failed to transfer user';
      toast.error(errorMessage);
    }
  };

  const deleteUser = async (userId: number, reason: string) => {
    try {
      await api.delete(`/admin/users/${userId}`, {
        data: { reason }
      });
      toast.success('User deleted successfully');
      fetchUsers();
      fetchUserStats();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.message || (error as any)?.message || 'Failed to delete user';
      toast.error(errorMessage);
    }
  };

  const handleDeleteUser = (user: User) => {
    const reason = prompt('Please provide a reason for deleting this user:');
    if (reason && reason.trim()) {
      deleteUser(user.id, reason.trim());
    }
  };

  const handleViewUser = async (user: User) => {
    setSelectedUser(user);
    setShowUserModal(true);
    await fetchUserDetails(user.id);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <ShieldCheckIcon className="w-4 h-4" />;
      case 'garage_owner':
        return <UserGroupIcon className="w-4 h-4" />;
      case 'employee':
        return <UserIcon className="w-4 h-4" />;
      case 'customer':
        return <UserIcon className="w-4 h-4" />;
      default:
        return <UserIcon className="w-4 h-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800';
      case 'garage_owner':
        return 'bg-blue-100 text-blue-800';
      case 'employee':
        return 'bg-green-100 text-green-800';
      case 'customer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const formatRole = (role: string) => {
    return role.replace('_', ' ').split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <UsersIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#008B8B] mr-2 sm:mr-3" />
                User Management
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage all platform users across garages</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <ShieldCheckIcon className="w-6 sm:w-8 h-6 sm:h-8 text-purple-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Super Admins</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {userStats?.by_role?.super_admin || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <UserGroupIcon className="w-6 sm:w-8 h-6 sm:h-8 text-blue-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Garage Owners</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {userStats?.by_role?.garage_owner || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <UserIcon className="w-6 sm:w-8 h-6 sm:h-8 text-green-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Employees</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {userStats?.by_role?.employee || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <UsersIcon className="w-6 sm:w-8 h-6 sm:h-8 text-gray-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Customers</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {userStats?.by_role?.customer || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="relative lg:col-span-2">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 sm:pl-10 w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
            >
              <option value="">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="garage_owner">Garage Owner</option>
              <option value="employee">Employee</option>
              <option value="customer">Customer</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <div className="text-center lg:text-right">
              <span className="text-xs sm:text-sm text-gray-600">
                Total: <span className="font-bold text-gray-900">{total}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Garage
                  </th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008B8B] mx-auto"></div>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 sm:h-10 w-8 sm:w-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <UserIcon className="w-4 sm:w-6 h-4 sm:h-6 text-gray-600" />
                          </div>
                          <div className="ml-2 sm:ml-4">
                            <div className="text-xs sm:text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-xs sm:text-sm text-gray-500">ID: {user.id}</div>
                            <div className="sm:hidden text-xs text-gray-500 mt-1">
                              {user.email || user.phone_number || '-'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900">{user.email || '-'}</div>
                          <div className="text-sm text-gray-500">{user.phone_number || '-'}</div>
                        </div>
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                        <span className={`px-1 sm:px-2 py-0.5 sm:py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${getRoleColor(user.role)}`}>
                          <span className="hidden sm:inline">{getRoleIcon(user.role)}</span>
                          <span className="sm:ml-1">{formatRole(user.role)}</span>
                        </span>
                      </td>
                      <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.garage?.name || '-'}</div>
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                        <span className={`px-1 sm:px-2 py-0.5 sm:py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(user.is_active)}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-1 sm:space-x-2">
                          <button
                            onClick={() => handleViewUser(user)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <EyeIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                          </button>
                          <button
                            onClick={() => resetUserPassword(user.id)}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Reset Password"
                          >
                            <KeyIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                          </button>
                          {user.is_active ? (
                            <button
                              onClick={() => updateUserStatus(user.id, false, 'Deactivated by admin')}
                              className="text-red-600 hover:text-red-900"
                              title="Deactivate"
                            >
                              <XCircleIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                            </button>
                          ) : (
                            <button
                              onClick={() => updateUserStatus(user.id, true, 'Activated by admin')}
                              className="text-green-600 hover:text-green-900"
                              title="Activate"
                            >
                              <CheckCircleIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                            </button>
                          )}
                          {user.role !== 'super_admin' && (
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {total > 20 && (
            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * 20 >= total}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(page - 1) * 20 + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(page * 20, total)}</span> of{' '}
                    <span className="font-medium">{total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page * 20 >= total}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* User Details Modal */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">User Details</h3>
                  <button
                    onClick={() => setShowUserModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>
                
                {userDetail ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <p className="text-sm text-gray-900">{userDetail.user.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <p className="text-sm text-gray-900">{userDetail.user.email || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Phone</label>
                        <p className="text-sm text-gray-900">{userDetail.user.phone_number || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Role</label>
                        <p className="text-sm text-gray-900">{formatRole(userDetail.user.role)}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(userDetail.user.is_active)}`}>
                          {userDetail.user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Garage</label>
                        <p className="text-sm text-gray-900">{userDetail.user.garage?.name || '-'}</p>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Statistics</label>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>Activity Count: {userDetail.activity_count}</div>
                        {userDetail.assigned_jobs !== undefined && (
                          <div>Assigned Jobs: {userDetail.assigned_jobs}</div>
                        )}
                        {userDetail.completed_jobs !== undefined && (
                          <div>Completed Jobs: {userDetail.completed_jobs}</div>
                        )}
                        {userDetail.vehicle_count !== undefined && (
                          <div>Vehicles: {userDetail.vehicle_count}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008B8B] mx-auto"></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Password Reset Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Password Reset</h3>
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">New temporary password:</p>
                  <p className="text-lg font-mono bg-gray-100 p-2 rounded border">{tempPassword}</p>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  The user will be required to change this password on next login.
                </p>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 bg-[#008B8B] text-white rounded hover:bg-[#006666]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default UserManagement;